import { createDatepickerTokens, createInputTokens } from '../tokens/components.js';
import { SEMANTIC, type SemanticTokens } from '../tokens/semantic.js';
import { fontWeight } from './../tokens/primitives';

/**
 * Datepicker Component Configuration
 */
export function createDatepicker(s: SemanticTokens) {
  const inputTokens = createInputTokens(s);
  const datepickerTokens = createDatepickerTokens(s);
  const rangeBg = `var(--nbx-datepicker-range-bg, ${datepickerTokens.date.rangeBg})`;
  const rangeFg = `var(--nbx-datepicker-range-fg, ${datepickerTokens.date.rangeFg})`;
  const dateHoverBg = `var(--nbx-datepicker-hover-bg, ${datepickerTokens.date.bgHover})`;
  return {
    colorScheme: {
      light: {
        dropdown: {
          background: datepickerTokens.dropdown.bg,
          hoverBackground: datepickerTokens.dropdown.bgHover,
          activeBackground: datepickerTokens.dropdown.bgActive,
          color: datepickerTokens.dropdown.fg,
          hoverColor: datepickerTokens.dropdown.fgHover,
          activeColor: datepickerTokens.dropdown.fgActive,
        },
        inputIcon: { color: datepickerTokens.inputIcon.fg },
        date: {
          selectedBackground: datepickerTokens.date.selectedBg,
          selectedColor: datepickerTokens.date.selectedFg,
        },
        today: {
          background: datepickerTokens.today.bgLight,
          color: datepickerTokens.today.fg,
        },
      },
    },
    panel: {
      background: datepickerTokens.panel.bg,
      borderColor: 'transparent',
      color: datepickerTokens.panel.fg,
      borderRadius: datepickerTokens.base.radius,
      shadow: datepickerTokens.panel.shadow,
      padding: datepickerTokens.base.padding,
    },
    header: {
      background: datepickerTokens.header.bg,
      borderColor: 'transparent',
      color: datepickerTokens.header.fg,
      padding: `0 0 ${datepickerTokens.header.padding} 0`,
    },
    title: { gap: datepickerTokens.title.gap, fontWeight: datepickerTokens.title.fontWeight },
    dropdown: {
      width: datepickerTokens.dropdown.size,
      borderColor: 'transparent',
      hoverBorderColor: 'transparent',
      activeBorderColor: 'transparent',
      borderRadius: datepickerTokens.dropdown.radius,
      focusRing: { width: '0', style: 'none', color: 'transparent', offset: '0', shadow: 'none' },
    },
    inputIcon: { color: datepickerTokens.inputIcon.fg },
    selectMonth: {
      hoverBackground: 'transparent',
      color: datepickerTokens.header.fg,
      hoverColor: datepickerTokens.header.fg,
      padding: datepickerTokens.nav.padding,
      borderRadius: datepickerTokens.nav.radius,
    },
    selectYear: {
      hoverBackground: 'transparent',
      color: datepickerTokens.header.fg,
      hoverColor: datepickerTokens.header.fg,
      padding: datepickerTokens.nav.padding,
      borderRadius: datepickerTokens.nav.radius,
    },
    group: { borderColor: 'transparent', gap: '0' },
    dayView: { margin: '0' },
    weekDay: {
      padding: datepickerTokens.weekDay.padding,
      color: datepickerTokens.weekDay.fg,
      fontWeight: datepickerTokens.weekDay.fontWeight,
    },
    date: {
      width: datepickerTokens.date.size,
      height: datepickerTokens.date.size,
      borderRadius: datepickerTokens.date.radius,
      padding: datepickerTokens.date.padding,
      hoverBackground: dateHoverBg,
      selectedBackground: datepickerTokens.date.selectedBg,
      rangeSelectedBackground: rangeBg,
      selectedColor: datepickerTokens.date.selectedFg,
      rangeSelectedColor: rangeFg,
      color: datepickerTokens.date.fg,
      hoverColor: datepickerTokens.date.fgHover,
      focusRing: { width: '0', style: 'none', color: 'transparent', offset: '0', shadow: 'none' },
    },
    monthView: { margin: `${datepickerTokens.month.padding} 0 0 0` },
    month: { borderRadius: datepickerTokens.month.radius, padding: datepickerTokens.month.padding },
    yearView: { margin: `${datepickerTokens.month.padding} 0 0 0` },
    year: { borderRadius: datepickerTokens.month.radius, padding: datepickerTokens.month.padding },
    buttonbar: {
      padding: `${datepickerTokens.timePicker.padding} 0 0 0`,
      borderColor: 'transparent',
    },
    timePicker: {
      padding: `${datepickerTokens.timePicker.padding} 0 0 0`,
      borderColor: 'transparent',
      gap: datepickerTokens.timePicker.gap,
    },
    css: () => `
        /* Input icon positioning - custom layout not supported by tokens */
        .p-datepicker-input {
          width: 100%;
        }

        .p-datepicker-input-icon-container {
          position: absolute;
          top: 43%;
          left: ${datepickerTokens.base.padding};
          pointer-events: none;
        }
        
        .p-datepicker-input-icon-container .p-icon {
          width: ${datepickerTokens.dropdown.size};
          height: ${datepickerTokens.dropdown.size};
          pointer-events: auto;
        }
        
        .p-inputtext:has(+ .p-datepicker-input-icon-container) {
          padding-left: calc(${datepickerTokens.dropdown.size} + ${datepickerTokens.base.padding} * 1.25);
          width: 100%;
        }
        
        /* Calendar table structure */
        .p-datepicker-calendar table {
          width: 100%;
          border-collapse: collapse;
        }
        
        /* Week header border */
        .p-datepicker-calendar thead {
          border-bottom: 1px solid ${datepickerTokens.border.default};
          border-top: 1px solid ${datepickerTokens.border.default};
        }
        
        /* Day cell structure for proper spacing */
        .p-datepicker-day-cell {
          padding: 2px;
          box-sizing: border-box;
          position: relative;
        }
        
        /* Day border and transition effects */
        .p-datepicker-day {
          margin: 0 auto;
          width: ${datepickerTokens.day.size};
          height: ${datepickerTokens.day.size};
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: ${datepickerTokens.day.fontWeight};
          border: ${datepickerTokens.day.borderWidth} solid transparent;
          transition: all ${datepickerTokens.day.transition};
          text-align: center;
          position: relative;
          z-index: 1;
        }
        
        /* Selected date border */
        :root .p-datepicker-day-selected {
          border: 1px solid ${datepickerTokens.date.selectedBorder};
        }
        
        /* The guest site's canvas is white, so the range band, hover, month frames and today ring
           keep the light neutral tone the picker used before the guest mode got its own canvas,
           which is also the tone of the guest site's date picker design. */
        [data-nbx-mode='ssr'] {
          --nbx-datepicker-range-bg: var(--nbx-color-neutral-50);
          --nbx-datepicker-hover-bg: var(--nbx-color-neutral-50);
          --nbx-datepicker-month-border: var(--nbx-color-neutral-50);
          --nbx-datepicker-today-border: var(--nbx-color-neutral-50);
        }

        /* Range dates border */
        :root .p-datepicker-day-selected-range {
          border: ${datepickerTokens.day.borderWidth} solid var(--nbx-datepicker-range-border, ${datepickerTokens.date.rangeBorder});
          border-radius: 0;
          font-weight: ${datepickerTokens.rangeDay.fontWeight};
        }
        
        :root .p-datepicker-title {
          
        }

        :root .p-datepicker-select-month {
        
        }
        
        /* Range start - rounded left only */
        :root .p-datepicker-day-cell:has(+ .p-datepicker-day-cell .p-datepicker-day-selected-range) .p-datepicker-day-selected {
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        }
        
        /* Range end - rounded right only */
        :root .p-datepicker-day-cell:has(.p-datepicker-day-selected-range) + .p-datepicker-day-cell .p-datepicker-day-selected {
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
        }
        
        /* Fill background between range dates - 
           Use a pseudo-element track that matches the day circle height */
        :root .p-datepicker-day-cell:has(.p-datepicker-day-selected-range)::after,
        :root .p-datepicker-day-cell:has(+ .p-datepicker-day-cell .p-datepicker-day-selected-range):has(.p-datepicker-day-selected)::after,
        :root .p-datepicker-day-cell:has(.p-datepicker-day-selected-range) + .p-datepicker-day-cell:has(.p-datepicker-day-selected)::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 0;
          right: 0;
          height: ${datepickerTokens.day.size};
          transform: translateY(-50%);
          background: ${rangeBg};
          z-index: 0;
        }
        
        /* Range start cell - track from center to right only */
        :root .p-datepicker-day-cell:has(+ .p-datepicker-day-cell .p-datepicker-day-selected-range):has(.p-datepicker-day-selected)::after {
          left: 50%;
          right: 0;
          border-radius: 0;
        }
        
        /* Range end cell - track from left to center only */
        :root .p-datepicker-day-cell:has(.p-datepicker-day-selected-range) + .p-datepicker-day-cell:has(.p-datepicker-day-selected)::after {
          left: 0;
          right: 50%;
          border-radius: 0;
        }

        /* 2-day range: connect two adjacent selected days (no intermediate range days) */

        /* Remove right border-radius from first selected when directly followed by another selected */
        :root .p-datepicker-day-cell:has(.p-datepicker-day-selected):has(+ .p-datepicker-day-cell .p-datepicker-day-selected) .p-datepicker-day {
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        }

        /* Remove left border-radius from second selected when directly preceded by another selected */
        :root .p-datepicker-day-cell:has(.p-datepicker-day-selected) + .p-datepicker-day-cell:has(.p-datepicker-day-selected) .p-datepicker-day {
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
        }

        /* Track background: right half of first selected cell */
        :root .p-datepicker-day-cell:has(.p-datepicker-day-selected):has(+ .p-datepicker-day-cell .p-datepicker-day-selected)::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          right: 0;
          height: ${datepickerTokens.day.size};
          transform: translateY(-50%);
          background: ${datepickerTokens.date.selectedBg};
          z-index: 0;
        }

        /* Track background: left half of second selected cell */
        :root .p-datepicker-day-cell:has(.p-datepicker-day-selected) + .p-datepicker-day-cell:has(.p-datepicker-day-selected)::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 0;
          right: 50%;
          height: ${datepickerTokens.day.size};
          transform: translateY(-50%);
          background: ${datepickerTokens.date.selectedBg};
          z-index: 0;
        }
        
        /* Today indicator - custom border styling */
        :root .p-datepicker-today .p-datepicker-day:not(.p-datepicker-day-selected):not(.p-datepicker-day-selected-range) {
          background: transparent;
          border: 5px solid var(--nbx-datepicker-today-border, ${datepickerTokens.today.border});
          color: ${datepickerTokens.panel.fg};
        }
        
        /* Other month days - muted text */
        :root .p-datepicker-other-month {
          visibility: hidden;
        }

        /* Disabled state - icon color */
        .p-datepicker:has(.p-datepicker-input:disabled) .p-datepicker-input-icon-container {
          color: ${inputTokens.fgDisabled};
        }

        /* Chevron navigation buttons - action button styling */
        :root .p-datepicker-prev-button,
        :root .p-datepicker-next-button,
        :root .p-datepicker-increment-button.p-button,
        :root .p-datepicker-decrement-button.p-button {
          padding: ${datepickerTokens.nav.padding};
          border: none;
          border-radius: ${datepickerTokens.month.radius};
          background: ${datepickerTokens.nav.bg};
          color: ${datepickerTokens.nav.fg};
          transition: background-color ${datepickerTokens.day.transition}, color ${datepickerTokens.day.transition};
        }

        :root .p-datepicker-prev-button:hover,
        :root .p-datepicker-next-button:hover,
        :root .p-datepicker-increment-button.p-button:hover,
        :root .p-datepicker-decrement-button.p-button:hover {
          background: ${datepickerTokens.nav.bgHover};
          color: ${datepickerTokens.nav.fgHover};
          border: none;
        }

        :root .p-datepicker-prev-button:active,
        :root .p-datepicker-next-button:active,
        :root .p-datepicker-increment-button.p-button:active,
        :root .p-datepicker-decrement-button.p-button:active {
          background: ${datepickerTokens.nav.bgActive};
          color: ${datepickerTokens.nav.fgHover};
          border: none;
        }

        :root .p-datepicker-panel-inline {
          padding: 0;
        }

        :root .p-datepicker-panel-inline .p-datepicker-calendar-container {
          gap: ${datepickerTokens.panel.padding};
          flex-wrap: wrap;
        }

        :root .p-datepicker-panel-inline .p-datepicker-calendar-container .p-datepicker-calendar {
          border: 2px solid var(--nbx-datepicker-month-border, ${datepickerTokens.panel.borderColor});
          border-radius: ${datepickerTokens.base.radius};
          padding: ${datepickerTokens.header.padding};
        }

        :root .p-datepicker-panel-inline .p-datepicker-calendar-container  {
          display: flex;
          justify-content: center;
        }

        /* Today indicator - custom border styling */
        :root .p-datepicker-panel-inline .p-datepicker-today .p-datepicker-day:not(.p-datepicker-day-selected):not(.p-datepicker-day-selected-range) {
          background: transparent;
          border: 4px solid var(--nbx-datepicker-today-border, ${datepickerTokens.today.border});
          color: ${datepickerTokens.panel.fg};
        }

        @media (max-width: 350px) {
        :root .p-datepicker-panel-inline .p-datepicker-calendar-container {
          transform: scale(0.7);
        }
    }
      `,
  };
}
export const datepicker = createDatepicker(SEMANTIC);
